import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import MaterialReactTable from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import { getProveedorLedger } from "../../pago_proveedores/ApiServices";
import { formatMoneda } from "../../pago_proveedores/utils";

const formatearFecha = (value) => {
  if (!value) return "—";
  const dateStr = String(value).split("T")[0];
  const [year, month, day] = dateStr.split("-");
  if (!year || !month || !day) return "—";
  return `${day}/${month}/${year}`;
};

const esNotaCredito = (row) => {
  const tipo = String(row?.comprobante || row?.origen || "").toUpperCase();
  return tipo.includes("NOTA DE CREDITO") || tipo.includes("NC");
};

export default function CuentaCorrienteProveedor({ proveedor }) {
  const [movimientos, setMovimientos] = useState([]);
  const [saldos, setSaldos] = useState({ debito: 0, credito: 0, saldo: 0 });
  const [infoProveedor, setInfoProveedor] = useState(null);
  const [orden, setOrden] = useState("DESC");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cargarLedger = useCallback(async () => {
    if (!proveedor?.nro_cuit) {
      setMovimientos([]);
      setSaldos({ debito: 0, credito: 0, saldo: 0 });
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getProveedorLedger(proveedor.nro_cuit, { orden });
      setMovimientos(Array.isArray(data.movimientos) ? data.movimientos : []);
      setSaldos({
        debito: Number(data.saldos?.debito ?? 0),
        credito: Number(data.saldos?.credito ?? 0),
        saldo: Number(data.saldos?.saldo ?? 0),
      });
      setInfoProveedor(data.proveedor || null);
    } catch (err) {
      setError(err.message || "No se pudo cargar la cuenta corriente");
      setMovimientos([]);
      setSaldos({ debito: 0, credito: 0, saldo: 0 });
    } finally {
      setLoading(false);
    }
  }, [proveedor?.nro_cuit, orden]);

  useEffect(() => {
    cargarLedger();
  }, [cargarLedger]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "fecha_emision",
        header: "Fecha",
        size: 100,
        muiTableBodyCellProps: { align: "center" },
        Cell: ({ cell }) => formatearFecha(cell.getValue()),
        Footer: () => (
          <Typography fontWeight={800}>TOTALES</Typography>
        ),
      },
      {
        accessorKey: "origen",
        header: "Tipo",
        size: 150,
        Cell: ({ row }) => row.original.origen || row.original.comprobante || "—",
      },
      {
        accessorKey: "nro_comprobante",
        header: "Comprobante",
        size: 140,
      },
      {
        accessorKey: "fecha_vencimiento",
        header: "Vence el",
        size: 110,
        muiTableBodyCellProps: { align: "center" },
        muiTableHeadCellProps: { align: "center" },
        Cell: ({ cell }) => formatearFecha(cell.getValue()),
      },
      {
        accessorKey: "debe",
        header: "Debe",
        size: 130,
        muiTableBodyCellProps: { align: "right" },
        muiTableHeadCellProps: { align: "right" },
        muiTableFooterCellProps: { align: "right" },
        Cell: ({ cell }) => {
          const value = Number(cell.getValue());
          if (!value) return null;
          return (
            <Typography sx={{ color: "success.main", fontWeight: 600 }}>
              {formatMoneda(value)}
            </Typography>
          );
        },
        Footer: () => (
          <Typography sx={{ color: "success.main", fontWeight: 700 }}>
            {formatMoneda(saldos.debito)}
          </Typography>
        ),
      },
      {
        accessorKey: "haber",
        header: "Haber",
        size: 130,
        muiTableBodyCellProps: { align: "right" },
        muiTableHeadCellProps: { align: "right" },
        muiTableFooterCellProps: { align: "right" },
        Cell: ({ cell }) => {
          const value = Number(cell.getValue());
          if (!value) return null;
          return (
            <Typography sx={{ color: "error.main", fontWeight: 600 }}>
              {formatMoneda(value)}
            </Typography>
          );
        },
        Footer: () => (
          <Typography sx={{ color: "error.main", fontWeight: 700 }}>
            {formatMoneda(saldos.credito)}
          </Typography>
        ),
      },
      {
        accessorKey: "saldo_cta_cte",
        header: "Saldo",
        size: 140,
        muiTableBodyCellProps: { align: "right" },
        muiTableHeadCellProps: { align: "right" },
        muiTableFooterCellProps: { align: "right" },
        Cell: ({ cell }) => {
          const value = Number(cell.getValue());
          const color = value >= 0 ? "success.main" : "error.main";
          return (
            <Typography sx={{ color, fontWeight: 600 }}>
              {formatMoneda(value)}
            </Typography>
          );
        },
        Footer: () => (
          <Typography
            sx={{
              color: saldos.saldo >= 0 ? "success.main" : "error.main",
              fontWeight: 700,
            }}
          >
            {formatMoneda(saldos.saldo)}
          </Typography>
        ),
      },
      {
        accessorKey: "saldoImputado",
        header: "Pendiente",
        size: 160,
        Cell: ({ row }) => {
          const saldoPendiente = Number(row.original.saldoImputado);
          const estado = String(row.original.estado || "").toLowerCase();

          if (
            !Number.isFinite(saldoPendiente) ||
            saldoPendiente <= 0 ||
            esNotaCredito(row.original) ||
            estado === "anulado"
          ) {
            return null;
          }

          return (
            <Chip
              label={formatMoneda(saldoPendiente)}
              size="small"
              sx={{
                height: 22,
                fontSize: "0.72rem",
                fontWeight: 700,
                borderRadius: "6px",
                backgroundColor: "rgba(245, 158, 11, 0.12)",
                color: "#B45309",
                border: "1px solid rgba(245, 158, 11, 0.25)",
              }}
            />
          );
        },
      },
      {
        accessorKey: "estado",
        header: "Estado",
        size: 140,
      },
    ],
    [saldos.credito, saldos.debito, saldos.saldo]
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  const titular =
    infoProveedor?.razon_social || proveedor?.razon_social || "Proveedor";

  return (
    <Box sx={{ py: 2 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Cuenta corriente
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {titular}
            {infoProveedor?.nro_cuit ? ` · CUIT ${infoProveedor.nro_cuit}` : ""}
          </Typography>
        </Box>

        <ToggleButtonGroup
          size="small"
          exclusive
          value={orden}
          onChange={(_e, value) => {
            if (value) setOrden(value);
          }}
          aria-label="Orden de movimientos"
        >
          <ToggleButton value="DESC">
            <SwapVertIcon sx={{ mr: 0.5, fontSize: 18 }} />
            Más recientes
          </ToggleButton>
          <ToggleButton value="ASC">Más antiguos</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error ? (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            border: "1px solid",
            borderColor: "error.light",
            borderRadius: 2,
            bgcolor: "error.50",
          }}
        >
          <Typography color="error.main" sx={{ mb: 1 }}>
            {error}
          </Typography>
          <Button size="small" variant="outlined" onClick={cargarLedger}>
            Reintentar
          </Button>
        </Paper>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <TrendingUpIcon sx={{ color: "success.main" }} />
              <Typography variant="subtitle2" color="text.secondary">
                Total débitos
              </Typography>
            </Box>
            <Typography variant="h5" fontWeight={700} color="success.main">
              {formatMoneda(saldos.debito)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <TrendingDownIcon sx={{ color: "error.main" }} />
              <Typography variant="subtitle2" color="text.secondary">
                Total créditos
              </Typography>
            </Box>
            <Typography variant="h5" fontWeight={700} color="error.main">
              {formatMoneda(saldos.credito)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <AccountBalanceWalletIcon color="primary" />
              <Typography variant="subtitle2" color="text.secondary">
                Saldo actual
              </Typography>
            </Box>
            <Typography
              variant="h5"
              fontWeight={700}
              color={saldos.saldo >= 0 ? "success.main" : "error.main"}
            >
              {formatMoneda(saldos.saldo)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <MaterialReactTable
        columns={columns}
        data={movimientos}
        localization={MRT_Localization_ES}
        enableColumnActions={false}
        enableColumnFilters={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableHiding={false}
        enablePagination={movimientos.length > 10}
        initialState={{
          density: "compact",
          pagination: { pageSize: 15, pageIndex: 0 },
        }}
        muiTableHeadCellProps={{
          align: "left",
          sx: {
            fontSize: 14,
            color: "white",
            fontWeight: "normal",
            backgroundColor: "#2471A3",
            borderColor: "black",
          },
        }}
        muiTableBodyCellProps={{
          align: "left",
          sx: { borderColor: "black" },
        }}
        muiTableFooterCellProps={{
          sx: {
            backgroundColor: "rgba(235, 235, 235, 0.9)",
            fontWeight: 700,
          },
        }}
        muiTablePaperProps={{
          elevation: 0,
          sx: { border: "1px solid", borderColor: "divider", borderRadius: 2 },
        }}
        renderTopToolbarCustomActions={() => (
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Movimientos ({movimientos.length})
          </Typography>
        )}
      />
    </Box>
  );
}
